// English translations
import type { Translations } from "./vi";

export const en: Translations = {
  common: {
    appName: "The PickleHub",
    loading: "Loading...",
    error: "An error occurred",
    retry: "Retry",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    viewAll: "View all",
    share: "Share",
    copyLink: "Copy link",
    copied: "Copied!",
    noResults: "No results",
    comingSoon: "Coming soon",
  },
  privacy: {
    title: "Privacy Policy – The Pickle Hub",
    intro: {
      title: "Introduction",
      description: "The Pickle Hub is a video, livestream and Pickleball tournament organization platform.",
      commitment: "We respect our users' privacy and are committed to protecting personal data.",
    },
    dataCollection: {
      title: "Information We Collect",
      description: "We only collect data necessary to provide our services:",
      items: {
        email: "Email address",
        displayName: "Display name / username",
        avatar: "Profile picture (if signing in with Google)",
        usage: "Usage data related to tournaments, brackets, videos and livestreams",
      },
    },
    purpose: {
      title: "How We Use Your Data",
      items: {
        auth: "User account authentication",
        tournament: "Allow creation and management of tournaments",
        display: "Display leaderboards and match results",
        improve: "Improve user experience",
      },
    },
    sharing: {
      title: "Data Sharing",
      description: "We do not sell or share personal data with third parties. We only share with:",
      items: {
        oauth: "Authentication providers (Google) for sign-in",
        legal: "Legal authorities when required by law",
      },
    },
    security: {
      title: "Data Security",
      items: {
        storage: "Data is stored securely on cloud infrastructure",
        measures: "Industry-standard security measures are applied",
        access: "Data access is limited to essential personnel only",
      },
    },
    rights: {
      title: "Your Rights",
      description: "You have the right to:",
      items: {
        view: "View your personal information",
        edit: "Request correction or deletion of your account",
        stop: "Stop using the service at any time",
      },
    },
    contact: {
      title: "Contact",
      description: "If you have questions about this privacy policy, please contact:",
    },
    effective: {
      text: "This policy is effective as of {date}.",
      update: "This policy may be updated as the platform develops new features.",
    },
  },
  terms: {
    title: "Terms of Service – The Pickle Hub",
    intro: {
      title: "Introduction",
      description: "Welcome to The Pickle Hub. By using our services, you agree to comply with the following terms.",
      acceptance: "If you do not agree with any of these terms, please do not use our services.",
    },
    account: {
      title: "Account Terms",
      items: {
        age: "You must be at least 13 years old to use our services",
        accuracy: "Registration information must be accurate and truthful",
        security: "You are responsible for maintaining the security of your account",
        responsibility: "You are responsible for all activities under your account",
      },
    },
    acceptableUse: {
      title: "Acceptable Use",
      description: "When using The Pickle Hub, you agree to:",
      items: {
        noIllegal: "Not engage in illegal activities",
        noHarassment: "Not harass, threaten, or insult others",
        noSpam: "Not spam or send unwanted content",
        noImpersonation: "Not impersonate others or organizations",
        noMalware: "Not distribute malware or harmful software",
      },
    },
    userContent: {
      title: "User Content",
      description: "Regarding content you upload:",
      items: {
        ownership: "You retain ownership of content you create",
        license: "You grant us the right to display content on the platform",
        moderation: "We reserve the right to remove content that violates our policies",
      },
    },
    intellectualProperty: {
      title: "Intellectual Property",
      description: "The Pickle Hub brand, logo, and content are protected by intellectual property laws. You may not copy or use them without permission.",
    },
    liability: {
      title: "Limitation of Liability",
      description: "The Pickle Hub provides services with the following limitations:",
      items: {
        asIs: "Services are provided \"as is\"",
        noWarranty: "We do not guarantee uninterrupted or error-free service",
        limitation: "We are not liable for indirect damages",
      },
    },
    termination: {
      title: "Termination",
      description: "Regarding termination of services:",
      items: {
        userRight: "You may delete your account at any time",
        platformRight: "We may suspend accounts that violate terms",
        effect: "After termination, your data will be deleted per our privacy policy",
      },
    },
    changes: {
      title: "Changes to Terms",
      description: "We may update these terms. Continued use of services after changes means you accept the new terms.",
    },
    contact: {
      title: "Contact",
      description: "If you have questions about these terms, please contact:",
    },
    effective: {
      text: "These terms are effective as of {date}.",
      update: "Terms may be updated as the platform develops.",
    },
  },
  account: {
    changePassword: "Change Password",
    changePasswordDescription: "Enter a new password to update your account.",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    passwordChanged: "Password changed successfully",
    passwordChangeError: "Failed to change password. Please try again.",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters",
  },
  nav: {
    home: "Home",
    live: "Live",
    videos: "Videos",
    tournaments: "Tournaments",
    tools: "Tools",
    search: "Search",
    profile: "Profile",
    login: "Login",
    logout: "Logout",
    signup: "Sign up",
    creator: "Creator Studio",
    admin: "Admin",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  tools: {
    title: "Tools",
    description: "Tools to help organize Pickleball tournaments",
    quickTable: {
      title: "Quick Bracket",
      description: "Round robin and playoff brackets for casual tournaments",
    },
    teamMatch: {
      title: "Team Match (MLP)",
      description: "Team-based competition in Major League Pickleball style",
    },
    singleElimination: {
      title: "Single Elimination",
      description: "Simple knockout bracket",
    },
    doublesElimination: {
      title: "Doubles Elimination",
      description: "Double elimination with winners/losers bracket",
    },
    comingSoon: "Coming soon",
    flexTournament: {
      title: "Flex Tournament",
      description: "Create your own tournament structure. Fully flexible. No rules enforced.",
      subtitle: "Create your own tournament structure. Fully flexible. No rules enforced.",
      subtitleFull: "This is a user-created format, flexible with no rule restrictions. The creator has full control to arrange players, teams, groups and matches as needed for their own tracking.",
      create: "Create Tournament",
      tournamentName: "Tournament Name",
      tournamentNamePlaceholder: "Enter tournament name",
      addPlayers: "Add Players",
      addPlayersPlaceholder: "Enter player names (one per line)",
      addPlayersHint: "You can paste multiple names at once",
      playerPool: "Player Pool",
      addTeam: "+ Team",
      addGroup: "+ Group",
      addMatch: "+ Match",
      teamName: "Team name",
      groupName: "Group name",
      matchName: "Match name",
      matchType: {
        label: "Match Type",
        singles: "Singles",
        doubles: "Doubles",
      },
      stats: {
        playerStats: "Player Stats",
        pairStats: "Pair Stats",
        wins: "W",
        losses: "L",
        pointDiff: "+/-",
        rank: "#",
        name: "Name",
        pair: "Pair",
      },
      generateRR: "Generate Round Robin",
      unlisted: "Unlisted",
      public: "Public",
      slotA: "Side A",
      slotB: "Side B",
      noPlayers: "No players yet",
      noTeams: "No teams yet",
      noGroups: "No groups yet",
      noMatches: "No matches yet",
      dropPlayerHere: "Drag player or team here",
      dropTeamHere: "Drop team here",
      score: "Score",
      vs: "vs",
      deleteConfirm: "Are you sure you want to delete this?",
      myTournaments: "My Flex Tournaments",
      createNew: "Create New",
      noTournaments: "No tournaments yet. Create your first one!",
      viewTournament: "View",
      share: "Share",
      settings: "Settings",
      visibility: "Visibility",
      visibilityHint: "Public tournaments can be viewed by anyone with the link",
      deleteError: "Failed to delete",
      createError: "Failed to create tournament",
      createSuccess: "Tournament created!",
      updateSuccess: "Updated successfully",
      generating: "Generating...",
      rrGenerated: "Round robin matches generated",
      duplicatePlayer: "This player name already exists",
      duplicateInTeam: "This player is already in this team",
      duplicateInGroup: "This player/team is already in this group",
      groupTypeMismatch: "Group accepts players only or teams only",
      duplicateInMatch: "This player is already in this match",
      includeDoublesInSingles: "Include doubles matches",
      noDoublesStats: "No doubles stats yet",
      countsForStandings: "Include in standings",
      noGroupHint: "Create a group to see stats",
      playerPanel: "Player List",
      openPlayerPanel: "Open player list",
      teams: "Teams",
      wins: "wins",
      tabTeams: "Teams",
      tabGroups: "Groups",
      tabMatches: "Matches",
      groupTabTeams: "Teams",
      groupTabIndividuals: "Individuals",
      selectTeamsToShow: "Select teams to show individual stats",
      allTeams: "All",
      noPairStats: "No pair stats yet",
      childMatch: "Match",
      addChildMatch: "+ Individual Match",
      childMatches: "Individual Matches",
      selectPlayer: "Select player",
      assignToGroup: "Assign to group",
      noGroup: "No group",
      selectGroup: "Select group",
    },
  },
  auth: {
    login: "Log in",
    signup: "Create account",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    orContinueWith: "or continue with",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    loginSuccess: "Logged in successfully",
    logoutSuccess: "Logged out",
    signupSuccess: "Account created! Please check your email to verify.",
    invalidCredentials: "Invalid email or password",
    accessDenied: "You don't have permission to access this page",
    loginRequired: "Please log in to continue",
    continueWithGoogle: "Continue with Google",
    emailNotVerified: "Email not verified",
    emailNotVerifiedDesc: "Please check your email to verify your account before signing in.",
    resendVerification: "Resend verification email",
    verificationSent: "Verification email sent",
    verificationSentDesc: "Please check your inbox (and spam folder) to verify your account.",
    emailAlreadyUsed: "Email already in use",
    emailAlreadyUsedDesc: "Please sign in or use a different email.",
  },
  home: {
    hero: {
      title: "The Pickle Hub",
      subtitle: "Professional Pickleball Media Platform",
      description: "Live streaming and on-demand videos from top tournaments",
      cta: "Explore now",
      badge: "Livestream & Video",
      mainTitle: "ThePickleHub – Global Pickleball Platform",
      mainDescription: "Watch live pickleball tournaments, follow brackets, and connect with pickleball communities worldwide.",
    },
    sections: {
      liveNow: "Live now",
      latestVideos: "Latest videos",
      popularThisWeek: "Popular this week",
      tournaments: "Tournaments",
      organizations: "Organizations",
    },
    noLive: "No livestreams at the moment",
    noVideos: "No videos available",
    // SEO Feature blocks
    features: {
      livestreamTitle: "Pickleball Livestream",
      livestreamDesc: "Watch professional and amateur pickleball tournaments live. Follow exciting matches with live commentary from anywhere.",
      tournamentsTitle: "Tournaments & Brackets",
      tournamentsDesc: "Smart bracket tool with automatic round-robin or playoff scheduling. Manage tournaments easily with a user-friendly interface.",
      communityTitle: "Pickleball Community",
      communityDesc: "Connect with thousands of pickleball players worldwide. Register for tournaments, follow your favorite creators, and share your passion.",
    },
    // SEO Footer
    about: {
      title: "About ThePickleHub",
      description: "ThePickleHub is a comprehensive global pickleball platform for players and tournament organizers. We provide live tournament streaming, automatic bracket management tools, and a space for players to connect. Whether you're a beginner or experienced, ThePickleHub is where you watch, learn, and improve your pickleball skills.",
      watchLive: "Watch Livestream →",
      tournaments: "Tournaments →",
      freeTools: "Free Tools →",
    },
  },
  video: {
    watch: "Watch video",
    duration: "Duration",
    views: "views",
    likes: "likes",
    comments: "comments",
    publishedAt: "Published on",
    relatedVideos: "Related videos",
    shorts: "Shorts",
    longform: "Long-form",
    noDescription: "No description",
  },
  live: {
    live: "LIVE",
    watching: "watching",
    totalViews: "views",
    endedAt: "Ended at",
    scheduled: "Scheduled",
    ended: "Ended",
    startingSoon: "Starting soon",
    watchLive: "Watch live",
    noLivestreams: "No livestreams",
    scheduledFor: "Scheduled for",
    replay: "Replay",
    watchingTooltip: "Number of people watching live right now",
    totalViewsTooltip: "Total number of views since the stream started",
    previewRemaining: "{seconds}s preview left",
    previewEnded: "Preview ended",
    signupToWatch: "Sign up free to continue watching",
    loginToWatch: "Log in to watch",
    createAccount: "Create free account",
    // SEO Hub content
    hubTitle: "Pickleball Livestream",
    hubDescription: "Watch pickleball tournaments live on ThePickleHub – your destination for real-time pickleball action.",
    seo: {
      tournamentsTitle: "Watch Pickleball Tournaments Live",
      tournamentsDesc: "ThePickleHub brings you the best pickleball livestream experience worldwide. Watch live matches from professional pickleball tournaments, amateur competitions, and community events. Our platform features streams from top pickleball creators, featuring high-quality broadcasts of singles, doubles, and mixed doubles matches.",
      creatorsTitle: "Livestream From Top Pickleball Creators",
      creatorsDesc: "Follow your favorite pickleball content creators and never miss a live match. Our creators broadcast tournaments, training sessions, and exhibition matches regularly. Get real-time scores, commentary, and the excitement of live pickleball competition directly on your device.",
      upcomingTitle: "Upcoming & Ongoing Pickleball Livestreams",
      upcomingDesc: "Browse our schedule of upcoming livestreams and set reminders for matches you don't want to miss. Whether you're looking for recreational club tournaments or competitive league matches with pickleball tournament brackets, ThePickleHub has your pickleball livestream needs covered.",
    },
  },
  tournament: {
    title: "Tournaments",
    upcoming: "Upcoming",
    ongoing: "Ongoing",
    ended: "Ended",
    startDate: "Start date",
    endDate: "End date",
    viewTournament: "View tournament",
    relatedContent: "Related content",
    follow: "Follow",
    remindMe: "Remind me",
    liveNow: "Live now",
    scheduled: "Scheduled",
    replays: "Replays",
    videos: "Videos",
    allVideos: "All",
    shortVideos: "Short",
    longVideos: "Long",
    noLive: "No live streams right now",
    noScheduled: "No scheduled streams",
    noReplays: "No replays available",
    noVideos: "No videos yet",
    checkBackLater: "Check back later for new content",
    hubTitle: "Pickleball Tournament Software",
    hubDescription: "The complete platform for organizing and managing pickleball tournaments of any size.",
    seo: {
      createTitle: "Create, Manage and Run Pickleball Tournaments",
      createDesc: "ThePickleHub provides powerful pickleball tournament software designed specifically for organizers. Whether you're running a small club tournament or a large competitive event, our platform handles everything from player registration to bracket generation and live scoring.",
      formatsTitle: "Support for Brackets, Team Matches and Livestream",
      formatsDesc: "Our pickleball tournament software supports multiple formats including round robin, single elimination, double elimination, and pickleball team match format competitions. Integrate pickleball livestream directly into your tournament to reach a wider audience. Players can view brackets, check schedules, and follow match results in real-time from any device.",
      builtTitle: "Built for Real Pickleball Events",
      builtDesc: "Used by tournament directors and pickleball clubs worldwide, ThePickleHub's tournament software is built from real-world experience organizing competitive pickleball events. From amateur community tournaments to professional competitions, our platform scales to meet your needs with features like court scheduling, referee management, and automated standings calculations.",
    },
    openRegistration: "Open for registration",
    pairs: "pairs",
    players: "players",
  },
  organization: {
    title: "Organization",
    videos: "Videos",
    livestreams: "Livestreams",
    about: "About",
    noDescription: "No description available",
    noContent: "No content yet",
    followForUpdates: "Follow to get notified when new content is published",
  },
  comments: {
    title: "Comments",
    placeholder: "Write a comment...",
    submit: "Submit",
    loginToComment: "Log in to comment",
    noComments: "No comments yet",
    delete: "Delete comment",
    deleted: "Comment deleted",
  },
  creator: {
    dashboard: "Creator Studio",
    overview: "Overview",
    videos: "My videos",
    livestreams: "Livestreams",
    settings: "Settings",
    upload: "Upload video",
    createLive: "Create livestream",
    noOrganization: "You haven't been assigned to an organization. Please contact admin.",
    stats: {
      totalViews: "Total views",
      totalLikes: "Total likes",
      totalVideos: "Videos",
      totalLivestreams: "Livestreams",
    },
    video: {
      title: "Title",
      description: "Description",
      type: "Video type",
      short: "Short",
      long: "Long-form",
      status: "Status",
      draft: "Draft",
      published: "Published",
      hidden: "Hidden",
      tags: "Tags",
      tournament: "Tournament",
      thumbnail: "Thumbnail",
      uploadVideo: "Upload video",
      uploading: "Uploading...",
      uploadSuccess: "Upload successful!",
      uploadError: "Upload failed",
      selectFile: "Select video file",
      dragDrop: "or drag and drop here",
      generatingThumbnail: "Generating thumbnail...",
      thumbnailGenerated: "Thumbnail generated",
      regenerateThumbnail: "Regenerate thumbnail",
      thumbnailPreview: "Thumbnail preview",
    },
    livestream: {
      title: "Title",
      description: "Description",
      scheduledStart: "Scheduled start",
      streamKey: "Stream Key",
      rtmpUrl: "RTMP URL",
      playbackUrl: "Playback URL",
      copyStreamKey: "Copy Stream Key",
      goLive: "Go live",
      endStream: "End stream",
      createSuccess: "Livestream created!",
      streamInfo: "Stream info",
    },
  },
  admin: {
    dashboard: "Admin Dashboard",
    overview: "Overview",
    organizations: "Organizations",
    users: "Users",
    tournaments: "Tournaments",
    stats: {
      totalUsers: "Total users",
      totalOrganizations: "Total organizations",
      totalVideos: "Total videos",
      totalLivestreams: "Total livestreams",
    },
    organization: {
      create: "Create organization",
      edit: "Edit organization",
      name: "Organization name",
      slug: "Slug",
      logo: "Logo",
      description: "Description",
      creators: "Creators",
      addCreator: "Add creator",
      removeCreator: "Remove creator",
    },
    user: {
      email: "Email",
      displayName: "Display name",
      role: "Role",
      organization: "Organization",
      changeRole: "Change role",
      assignOrg: "Assign organization",
      viewer: "Viewer",
      creator: "Creator",
      admin: "Admin",
    },
    tournament: {
      create: "Create tournament",
      edit: "Edit tournament",
      name: "Tournament name",
      slug: "Slug",
      startDate: "Start date",
      endDate: "End date",
      status: "Status",
      description: "Description",
    },
    moderation: {
      title: "Moderation",
      pendingReview: "Pending review",
      approved: "Approved",
      rejected: "Rejected",
      approve: "Approve",
      reject: "Reject",
      hide: "Hide content",
      reportedContent: "Reported content",
    },
    settings: {
      title: "System Settings",
      livestreamGate: "Livestream Settings",
      requireLogin: "Require login to watch",
      requireLoginDesc: "Viewers must log in or sign up to watch livestreams after preview time",
      previewDuration: "Preview duration",
      previewDurationDesc: "Seconds allowed before requiring login",
      appliesTo: "Applies to",
      appliesToAll: "All",
      appliesToLive: "Live only",
      appliesToReplay: "Replay only",
      savedSuccess: "Settings saved successfully",
      geoBlock: "Geo Blocking",
      geoBlockEnabled: "Enable Geo Blocking",
      geoBlockEnabledDesc: "Block users from selected countries from watching livestreams and videos",
      blockedCountries: "Blocked Countries",
      blockedCountriesDesc: "List of country codes (ISO 3166-1 alpha-2), separated by commas",
    },
    viewers: {
      title: "Livestream Viewers",
      description: "Real-time viewer list for active livestreams",
      selectLivestream: "Select Livestream",
      selectPlaceholder: "Choose a livestream to view...",
      noActiveLivestreams: "No active livestreams",
      viewerList: "Viewers",
      viewer: "Viewer",
      watching: "watching",
      connected: "Connected",
      disconnected: "Connecting...",
      noViewers: "No viewers yet",
      anonymous: "Anonymous",
      loggedIn: "Logged in",
      joinedAt: "Joined at",
      type: "Type",
    },
  },
  geoBlock: {
    title: "Content Not Available",
    description: "This content is not available in your region.",
  },
  player: {
    tapToPlayVideo: "Tap to play video",
    tapToWatchLive: "Tap to watch live",
    notReady: "Video not ready",
    playbackError: "Unable to play",
    playbackErrorDesc: "Cannot play on this browser. Try reloading or switching networks.",
    reconnecting: "Reconnecting...",
    connectionLost: "Connection lost, retrying...",
    autoRetry: "Auto-retrying in {seconds} seconds",
    retryFailed: "Could not reconnect. Please try again.",
  },
  errors: {
    notFound: "Page not found",
    notFoundDesc: "The page you're looking for doesn't exist.",
    serverError: "Server error",
    serverErrorDesc: "Something went wrong. Please try again later.",
    networkError: "Network error",
    networkErrorDesc: "Unable to connect to the server.",
    goHome: "Go home",
    error: "Error",
  },
  ads: {
    advertisement: "Advertisement",
    skip: "Skip",
  },
  search: {
    title: "Search",
    placeholder: "Search videos, livestreams or tournaments...",
    filterAll: "All",
    allTournaments: "All tournaments",
    sortNewest: "Newest",
    sortUpcoming: "Upcoming",
    noResults: "No results found",
    tryDifferent: "Try a different keyword",
    resultsCount: "Found {count} results",
    enterKeyword: "Enter a keyword to search",
    tabs: {
      all: "All",
      videos: "Videos",
      livestreams: "Livestreams",
      tournaments: "Tournaments",
    },
  },
  follow: {
    follow: "Follow",
    following: "Following",
    unfollow: "Unfollow",
  },
  notifications: {
    title: "Notifications",
    noNotifications: "No notifications",
    noNotificationsDesc: "Follow creators or tournaments to get notified of new livestreams",
    unread: "unread",
    markAllRead: "Mark all as read",
    livestreamScheduled: "scheduled a new livestream",
    livestreamLive: "is now live!",
  },
  chat: {
    title: "Chat",
    send: "Send",
    placeholder: "Type a message...",
    signInToChat: "Sign in to chat",
    chatDisabled: "Chat is disabled",
    slowMode: "Slow mode",
    slowModeWait: "Please wait before sending another message",
    settings: "Chat settings",
    mute: "Mute",
    userMuted: "User muted",
    youAreMuted: "You are muted until",
    delete: "Delete",
    enableChat: "Enable chat",
    disableChat: "Disable chat",
    noMessages: "No messages yet",
    off: "Off",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
    sendError: "Unable to send message",
    sending: "Sending...",
    sendFailed: "Failed to send",
    retry: "Retry",
    copy: "Copy",
    loadOlder: "Load older messages",
    newMessages: "new messages",
    nickname: "Nickname",
    nicknamePlaceholder: "Enter your nickname",
    nicknameUpdated: "Nickname updated",
    nicknameError: "Nickname error",
    nicknameLengthError: "Nickname must be 2-30 characters",
    nextEdit: "Next edit",
    pin: "Pin",
    unpin: "Unpin",
    pinnedMessage: "Pinned message",
    topChatters: "Top chatters",
    messages: "messages",
  },
  analytics: {
    title: "Analytics",
    description: "Track your content performance",
    totalViews: "Total Views",
    totalLivestreams: "Total Livestreams",
    totalVideos: "Total Videos",
    followers: "Followers",
    viewsOverTime: "Views Over Time",
    topContent: "Top Content",
    viewsByType: "Views by Type",
    views: "views",
    video: "Video",
    livestream: "Livestream",
    days: "days",
    noData: "No data yet",
    noDataDesc: "Start your first livestream or upload a video to track performance!",
    noViewData: "No views in this period",
    noContent: "No content yet",
  },
  share: {
    share: "Share",
    shareTitle: "Share content",
    copied: "Copied!",
    copiedDesc: "Link copied to clipboard",
    linkDesc: "Share this link so others can view the content",
    embedDesc: "Copy the iframe code to embed the video in your website",
    withTitle: "With title bar",
    withoutTitle: "Without title",
    qrDesc: "Scan the QR code to open on mobile or print for offline events",
    downloadQR: "Download QR",
    qrDownloaded: "Downloaded!",
    qrDownloadedDesc: "QR code saved to your device",
    socialLink: "Social share link (Facebook, Twitter...)",
    socialLinkDesc: "Use this link when sharing on social media to display correct title & image",
    directLink: "Direct link",
  },
  quickTable: {
    title: "Quick Bracket",
    description: "Free tool for bracket creation, match scheduling and casual tournament organization.",
    loginRequired: "Please log in to create a new bracket.",
    step1Title: "Step 1: Tournament Info",
    step1Desc: "Enter basic tournament information",
    step2Title: "Step 2: Select Format",
    step3Title: "Step 3: Create Brackets",
    tournamentName: "Tournament Name",
    tournamentNamePlaceholder: "E.g.: Summer Pickleball 2024",
    playerCount: "Number of Players (estimated)",
    playerCountPlaceholder: "E.g.: 16",
    requireRegistration: "Require player registration",
    requireRegistrationDesc: "Players must register and be approved before joining the bracket",
    requireSkillLevel: "Require skill level",
    requireSkillLevelDesc: "Players must declare their skill level (DUPR or self-described)",
    advancedSettings: "Advanced settings",
    autoApprove: "Auto-approve registrations",
    autoApproveDesc: "Players are approved immediately upon registration (not recommended)",
    doublesMode: "Doubles",
    doublesModeDesc: "Players register as pairs and can invite partners via link",
    registrationMessage: "Message to players when registering",
    registrationMessagePlaceholder: "E.g.: Organizers will verify skill levels based on declared ratings...",
    continue: "Continue",
    back: "Back",
    roundRobin: "Round Robin",
    roundRobinDesc: "Divide players into groups, each plays everyone in their group. Top from each group advances to Playoff.",
    largePlayoff: "Large Playoff",
    largePlayoffDesc: "Format for large tournaments. Rounds 1-2 track wins/losses and point differential, from round 3 onwards is single elimination.",
    recommended: "Recommended",
    notAvailableOver48: "Not available for >48 players",
    onlyAvailableOver32: "Only available for ≥32 players",
    status: {
      setup: "Setting up",
      groupStage: "Group Stage",
      playoff: "Playoff",
      completed: "Completed",
    },
    myTournaments: "My Tournaments",
    refereeTournaments: "Referee Tournaments",
    yourRegisteredTournaments: "Your Registered Tournaments",
    yourCompletedTournaments: "Completed Tournaments",
    openRegistrationTournaments: "Open Registration",
    openRegistrationTitle: "Open Registration Tournaments",
    expectedPlayers: "Expected {count} players",
    expectedPairs: "Expected {count} pairs",
    registering: "Registering",
    ongoing: "Ongoing",
    completed: "Completed",
    noOngoing: "No ongoing tournaments",
    noCompleted: "No completed tournaments yet",
    noTournaments: "You haven't created any tournaments yet",
    moreRemaining: "{count} more remaining",
    players: "players",
    approved: "Approved",
    pending: "Pending",
    rejected: "Rejected",
    showMore: "Show more",
    showLess: "Show less",
    showCompleted: "Show completed",
    hideCompleted: "Hide completed",
    referee: "Referee",
    noRating: "No rating",
    viewProfile: "View profile",
    editSkillLevel: "Edit skill level",
    btcNote: "Organizer note",
    actions: "Actions",
    approve: "Approve",
    reject: "Reject",
    cancelApproval: "Cancel approval",
    pendingRegistrations: "Pending Registrations",
    approvedPlayers: "Approved Players",
    rejectedRegistrations: "Rejected",
    loading: "Loading...",
    readyToBracket: "Ready to create brackets!",
    readyToBracketDesc: "{count} players approved. You can now create brackets.",
    createBracket: "Create Brackets",
    needMinPlayers: "Need at least 6 approved players. Currently {count}/6.",
    approveSelected: "Approve {count} players",
    // Registration / Pairing
    registration: {
      title: "Register",
      doublesTitle: "Register (Doubles)",
      displayName: "Display name",
      teamClub: "Team / Club (optional)",
      skillLevel: "Skill level",
      dupr: "DUPR",
      duprDesc: "Official DUPR rating system",
      otherSystem: "Other system",
      otherSystemDesc: "UTPR, APP, or other rating system",
      noRating: "I don't have a rating",
      noRatingDesc: "You will describe your skill level",
      systemName: "System name",
      systemNamePlaceholder: "E.g.: UTPR, APP, WPR...",
      duprScore: "DUPR score",
      duprLink: "DUPR profile link",
      skillScore: "Skill score",
      skillDescription: "Skill description",
      submit: "Register",
      submitting: "Submitting...",
      afterRegisterNote: "After registering, you will see available players to pair with.",
      waitingApproval: "Waiting for organizer approval",
      approved: "You have been approved",
      rejected: "Your registration was rejected",
      rejectedMessage: "Reason",
    },
    pairing: {
      pairUp: "Pair up",
      confirmPair: "Confirm pairing",
      confirmPairWith: "Send pairing request to",
      cancel: "Cancel",
      confirm: "Confirm",
      sending: "Sending...",
      waitingConfirm: "Waiting for confirmation",
      waitingYourConfirm: "Waiting for your confirmation",
      accept: "Accept",
      decline: "Decline",
      incomingRequests: "Someone is waiting to pair with you",
      outgoingRequests: "Sent pairing requests",
      availablePlayers: "Available players",
      noPartner: "No partner",
      hasPartner: "Has partner",
      removePartner: "Remove partner",
      teamLocked: "Tournament has started. Cannot modify team.",
    },
    allRegistered: "All registered players",
    yourTeam: "Your Team",
    teamLeader: "Player 1 (Team Leader)",
    partner: "Partner",
    manageTeam: "Manage team and pair with other players",
    registerDesc: "Register for tournament",
    alreadyRegistered: "You have already registered for this tournament",
    loginToRegister: "Please log in to register",
    login: "Log in",
    removePartnerConfirm: "Are you sure you want to remove partner from the team?",
    remove: "Remove",
    waitingForApproval: "Team leader can manage team and change partner.",
    waitingPartnerApproval: "Waiting for",
    skillDescOptions: ["Playing ~6 months", "Recreational", "Playing regularly ~1-2 years", "Tournament experience"],
    exampleClub: "E.g.: ABC Pickleball Club",
    exampleDuprLink: "https://mydupr.com/profile/...",
    exampleSkillDesc: "Or describe in your own way...",
    registeredPlayers: "Registered Players",
    noRegisteredPlayers: "No players registered yet",
    teamName: "Team Name",
    playerName: "Player Name",
    club: "Team/Club",
    notDeclared: "Not declared",
    hasPartnerStatus: "Has partner",
    noPartnerStatus: "No partner",
    statusHeader: "Status",
    // Playoff bracket
    playoff: {
      noMatches: "No playoff matches yet",
      champion: "Champion",
      match: "Match",
      final: "Final",
      semiFinal: "Semi-final",
      quarterFinal: "Quarter-final",
      round16: "Round of 16",
      round: "Round",
      group: "Group",
      enterNextRound: "Enter previous round results to unlock next round",
      inputScore: "Input",
      editScore: "Edit",
      openScoring: "Open scoring page",
      tieNotAllowed: "Tie scores not allowed",
    },
    // Quota / soft launch
    quota: {
      usage: "You have created {count}/3 tournaments",
      limitReached: "Soft launch limit reached",
      limitReachedDesc: "Soft launch limit: each account can create up to 3 tournaments.",
      contactUs: "Contact us",
    },
    // SEO content
    seo: {
      pageTitle: "Pickleball Bracket Generator",
      pageSubtitle: "Create tournament brackets for your pickleball events in seconds",
      createTitle: "Create Pickleball Tournament Brackets in Seconds",
      createDesc: "ThePickleHub's pickleball bracket generator makes tournament organization effortless. Simply enter your player count, choose your format, and get a complete bracket instantly. Our tool handles all the complexity of round robin scheduling, playoff seeding, and match ordering so you can focus on playing.",
      roundRobinTitle: "Round Robin Generator for Pickleball Events",
      roundRobinDesc: "The round robin format ensures every player or team competes against each other. Our generator automatically creates balanced groups, calculates the optimal number of matches, and schedules games to minimize wait times. Perfect for recreational tournaments where everyone wants maximum court time.",
      formatsTitle: "Support for Playoff, Elimination & Custom Formats",
      formatsDesc: "Beyond round robin, our pickleball bracket generator supports single elimination playoffs, group stage with knockout rounds, and large tournament formats for 50+ players. Track scores in real-time, share live brackets with participants, and crown your tournament champion with professional-quality bracket displays.",
    },
    // Additional UI text
    setup: {
      inputPlayers: "Enter player list",
      inputPlayersDesc: "Enter name and seed (optional)",
      shuffle: "Shuffle",
      playerNamePlaceholder: "Player name *",
      teamPlaceholder: "Team",
      seedPlaceholder: "Seed",
      addPlayer: "Add player",
      assignmentMethod: "Assignment method",
      autoMode: "Automatic",
      autoModeDesc: "System distributes evenly, avoids same team, spreads seeds",
      manualMode: "Manual",
      manualModeDesc: "Choose players for each group",
      tips: "Tips for good bracket creation:",
      tipTeam: "Enter Team to avoid same team in same group",
      tipSeed: "Number seeds (1 = strongest) to distribute top players evenly",
      tipAuto: "System will automatically distribute players into groups",
      tipManual: "You will assign players to groups in the next step",
      processing: "Processing...",
      continueManual: "Continue to assign groups",
      createBracketBtn: "Create bracket and assign groups",
      minPlayersError: "Need at least 2 players",
      notFound: "Bracket not found",
      notFoundDesc: "This bracket does not exist or has been deleted.",
      createdSuccess: "Bracket created successfully!",
      manualSuccess: "Manual assignment completed!",
      errorOccurred: "An error occurred, please try again",
    },
    manualAssignment: {
      title: "Manual group assignment",
      description: "Assign players to groups as you wish",
      guide: "Manual assignment guide",
      step1: "Click to select a player from the left list",
      step2: "Click on a group to add the player",
      step3: "Click on an assigned player to remove from group",
      unassigned: "Unassigned",
      allAssigned: "All players have been assigned",
      group: "Group",
      clickToAdd: "Click to add player",
      noPlayers: "No players yet",
      warning: "Warning",
      unbalancedGroups: "Groups have unbalanced player counts",
      sameTeamWarning: "players from same team",
      topSeedsWarning: "top seeds (1-2)",
      confirm: "Confirm assignment",
      remainingPlayers: "{count} players not yet assigned",
      emptyGroup: "Some groups have no players",
    },
    view: {
      groupStage: "Group Stage",
      shareSuccess: "Link copied!",
      deleteConfirm: "Are you sure you want to delete \"{name}\"? All data (players, matches, registrations...) will be permanently deleted.",
      deleteBtn: "Delete tournament",
      groupCompleteTitle: "Group stage complete!",
      groupCompleteDesc: "All matches have results. You can proceed to Playoff.",
      startPlayoff: "Start Playoff",
      registrationTab: "Registration",
      playoffTab: "Playoff",
      standings: "Standings",
      player: "Player",
      wins: "Wins",
      matches: "Matches",
      pointDiff: "Point Diff",
      actions: "Actions",
      matchList: "Match list",
      showTeam: "Show Team",
      courtTime: "Court & Time",
      addPlayer: "Add player",
      done: "Done",
      editGroups: "Edit groups",
      movePlayer: "Move group",
      removePlayer: "Remove player",
      removeConfirm: "Remove {name} from bracket?",
      movePlayerTitle: "Move player to another group",
      selectGroup: "Select group",
      moveBtn: "Move",
      movedSuccess: "Player moved",
      addPlayerTitle: "Add new player",
      playerName: "Player name",
      team: "Team (optional)",
      addBtn: "Add",
      addedSuccess: "Player added",
      removedSuccess: "Player removed",
      scoreUpdated: "Score updated",
      nextRoundCreated: "Next round created!",
      tournamentCompleted: "Tournament completed! 🎉",
      playoffCreated: "Playoff created!",
      onlyCreatorCanScore: "Only the creator or referee can enter scores.",
      wildcardTitle: "Select Wildcards",
      wildcardDesc: "Select {count} 3rd place players for Playoff",
      selectExactly: "Please select exactly {count} players",
      courtsTimeUpdated: "Courts & time updated",
      notFound: "Bracket not found",
      goBack: "Go back",
      group: "Group",
      court: "Court",
      inputScore: "Input",
      loading: "Loading...",
      deleteTournament: "Delete",
      deleteConfirmFull: "Are you sure you want to delete \"{name}\"? All data will be permanently deleted.",
      recommended: "Recommended",
      confirm: "Confirm",
      cancel: "Cancel",
      move: "Move",
      add: "Add",
      save: "Save",
      enterName: "Enter name",
      enterTeam: "Enter team",
      addToGroup: "Add to group",
      moveToGroup: "Move {name} to another group",
      selectTargetGroup: "Select target group",
      addPlayerName: "Player name",
      addPlayerTeam: "Team (optional)",
      editInlineScore: "Edit",
      inputInlineScore: "Input",
      openScoringPage: "Score",
      cancelEdit: "Cancel",
      saveScore: "Save",
      errorOccurred: "An error occurred",
      groupLabel: "Group",
      winsLabel: "wins",
      pointDiffLabel: "Point diff",
    },
    groups: {
      groups: "groups",
      playersPerGroup: "players/group",
      advanceToPlayoff: "advance to Playoff",
      noConfig: "No configuration available for {count} players.",
      tryOther: "Try a different player count.",
      quotaUsed: "{count}/{total} tournaments created",
    },
    // Match Scoring Page
    matchScoring: {
      loading: "Loading...",
      matchNotFound: "Match not found",
      loadError: "Failed to load data",
      scoreUpdateError: "Failed to update score",
      noPermission: "No access permission",
      noPermissionDesc: "You need to be the creator or referee to score",
      backToBracket: "Back to bracket",
      goBack: "Go back",
      live: "LIVE",
      ended: "Ended",
      match: "Match",
      final: "Final",
      semiFinal: "Semi-final",
      quarterFinal: "Quarter-final",
      round: "Round",
      claimSuccess: "You are now scoring this match",
      otherRefereeScoring: "Another referee is scoring this match",
      resetScore: "Reset score",
      resetConfirmTitle: "Reset score?",
      resetConfirmDesc: "Score will be reset to 0 — 0",
      resetSuccess: "Score reset",
      endMatch: "End match",
      endMatchConfirmTitle: "End match?",
      endMatchConfirmDesc: "Final result",
      finalResult: "Final result",
      winner: "Winner",
      tieWarning: "Tie score - no winner",
      confirm: "Confirm",
      cancel: "Cancel",
      endMatchSuccess: "Match ended",
      endMatchError: "Failed to end match",
      nextMatch: "Next match",
      noNextMatch: "No more matches",
    },
  },
  // Team Match SEO
  teamMatch: {
    pageTitle: "Pickleball Team Match Format",
    pageSubtitle: "Create and manage MLP-style team competitions",
    myTournaments: "My Tournaments",
    publicTournaments: "MLP Open Tournaments",
    noTournaments: "You haven't created any team tournaments yet",
    createFirst: "Create first tournament",
    loginPrompt: "Login to create and manage team tournaments",
    createNew: "Create new",
    viewDetails: "View details",
    confirmDelete: "Confirm delete?",
    confirmDeleteDesc: "Are you sure you want to delete \"{name}\"? This action cannot be undone.",
    cancel: "Cancel",
    delete: "Delete",
    teams: "teams",
    players: "players",
    formatRoundRobin: "Round Robin",
    formatSingleElim: "Single Elimination",
    formatRrPlayoff: "Round Robin + Playoff",
    statusSetup: "Setting up",
    statusRegistration: "Registration open",
    statusOngoing: "In progress",
    statusCompleted: "Completed",
    seo: {
      mlpTitle: "MLP-Style Pickleball Team Competition",
      mlpDesc: "The pickleball team match format brings professional-style team competition to your club or tournament. Inspired by Major League Pickleball (MLP), this format features teams competing across multiple game types including men's doubles, women's doubles, and mixed doubles. Create exciting team rivalries with our comprehensive team management tools.",
      lineupTitle: "Lineup, Dreambreaker & Rally Scoring",
      lineupDesc: "Our team match system supports full lineup management, allowing captains to strategically assign players to each game. When matches are tied, the dreambreaker format adds thrilling sudden-death gameplay. Rally scoring keeps every point exciting and ensures matches maintain competitive pace throughout the competition.",
      manageTitle: "Manage Team Matches for Pickleball Tournaments",
      manageDesc: "Whether organizing a casual inter-club match or a full league season, ThePickleHub's team match format tool handles all the complexity. Track team standings, manage rosters, schedule matches, and calculate results automatically. Combine with our bracket generator for complete tournament management and memorable pickleball team experiences.",
    },
    scoring: {
      title: "Scoring",
      saveScore: "Save score",
      saving: "Saving...",
      saved: "Score saved",
      scoreSaveError: "Could not save score",
      game: "Game",
      selectGame: "Select game to score",
      matchComplete: "Match completed",
      reset: "Reset",
      confirmReset: "Confirm reset score?",
    },
    setup: {
      title: "Create Team Tournament",
      subtitle: "MLP - Major League Pickleball style",
      stepBasicInfo: "Basic Info",
      stepGameTemplates: "Game Templates",
      stepDreambreaker: "DreamBreaker",
      stepFormat: "Format",
      tournamentName: "Tournament name",
      tournamentNamePlaceholder: "E.g.: MLP Spring 2026",
      playersPerTeam: "Players per team",
      teamCount: "Number of teams",
      requireRegistration: "Require registration",
      requireRegistrationDesc: "Captains create teams and invite members",
      requireMinGames: "Each player min 1 game",
      requireMinGamesDesc: "Lineup must use all team members",
      formatRoundRobin: "Round Robin",
      formatRoundRobinDesc: "All teams play each other",
      formatSingleElimination: "Single Elimination",
      formatSingleEliminationDesc: "Lose once and you're out",
      formatRrPlayoff: "Group Stage + Playoff",
      formatRrPlayoffDesc: "Round robin groups, then playoff",
      thirdPlaceMatch: "Third place match",
      thirdPlaceMatchDesc: "Match between semifinal losers",
      playoffTeams: "Playoff teams",
      playoffTeamsDesc: "Teams advancing from group stage",
      creating: "Creating...",
      createBtn: "Create tournament",
      invalidTeamCount: "Teams must be 4, 8, 16 or 32 for single elimination",
      loginRequired: "Login required",
      loginRequiredDesc: "You need to login to create team tournaments",
    },
    view: {
      overview: "Overview",
      teams: "Teams",
      matches: "Matches",
      standings: "Standings",
      settings: "Settings",
      copyLink: "Copy link",
      linkCopied: "Link copied!",
      yourTeam: "Your team",
      youAreCaptain: "You are the captain of this team",
      members: "Members",
      rosterFull: "Full roster",
      rosterIncomplete: "Incomplete",
      manageTeam: "Manage team",
      registerPrompt: "Register for tournament",
      createTeam: "Create team",
      inviteTeam: "Invite team",
      generateBracket: "Generate Bracket",
      generateMatches: "Generate matches",
      createGroups: "Create groups",
      startTournament: "Start tournament",
      createPlayoff: "Create Playoff",
      approveAllFirst: "Approve all teams before creating groups",
      needMinTeams: "Need at least {count} teams",
      matchesCreated: "{count} matches created",
      roundRobinComplete: "Round robin completed",
      btcActions: "Organizer Actions",
      approveBeforeGroups: "Approve {count} pending teams before creating groups",
      groupStage: "Group Stage",
      roundRobin: "Round Robin",
      playoffRound: "Playoff",
      seBracket: "Single Elimination Bracket",
      noMatches: "No matches yet",
      noMatchesDesc: "Match schedule will be created by the organizer",
      startConfirm: "Start tournament?",
      startConfirmDesc: "Once started, the tournament status will change to \"In progress\" and teams can no longer be added or removed.",
      start: "Start",
      teamsReady: "{count} teams ready",
      createSchedule: "Create schedule",
      deleteConfirm: "Delete tournament?",
      deleteConfirmDesc: "This action cannot be undone. All data will be permanently deleted.",
      notFound: "Tournament not found",
      notFoundDesc: "This tournament does not exist or has been deleted",
      backToList: "Back to list",
      deleteBtn: "Delete",
      registerForTournament: "Register for tournament",
      createTeamToJoin: "Create a new team to join",
      btcActionsTitle: "Organizer Actions",
      inviteTeamBtn: "Invite team",
      createGroupsBtn: "Create groups",
      generateBracketBtn: "Generate Bracket",
      roundStarted: "Round started",
      createPlayoffTitle: "Create Playoff",
      createPlayoffDesc: "Round robin completed",
      createScheduleBtn: "Create schedule",
      createScheduleDesc: "Invite teams or create match schedule",
      noMatchesEmpty: "No matches yet",
      noMatchesScheduleDesc: "Match schedule will be created by the organizer",
      startTournamentTitle: "Start tournament?",
      startTournamentDesc: "Once started, the tournament status will change to \"In progress\" and teams can no longer be added or removed.",
      startBtn: "Start",
      teamsReadyCount: "teams ready",
      generateScheduleBtn: "Create schedule",
      seBracketTitle: "Single Elimination Bracket",
      groupStageTitle: "Group Stage",
      roundRobinTitle: "Round Robin",
      approveBeforeBracket: "Approve all teams before creating bracket",
      inviteOrCreateSchedule: "Invite teams or create match schedule",
      inviteOrBracket: "Invite teams or create Single Elimination bracket",
      needMinTeamsForBracket: "Need at least 6 teams to create groups",
      cancelBtn: "Cancel",
      deleteAction: "Delete",
      errorOccurred: "Error",
      roundRobinDone: "Round robin completed — {count} teams qualified",
      teamsReadyForSchedule: "{count} teams ready",
      teamsReadySE: "{count} teams ready (Single Elimination)",
      startTournamentLabel: "Start tournament",
      matchesGeneratedCount: "{count} matches created",
      approveAllBeforeBracket: "Approve all teams before creating bracket",
      needMin6Groups: "Need at least 6 teams to create groups",
      approvePendingFirst: "Approve {count} pending teams before creating groups",
      approvePendingBracket: "Approve {count} pending teams before creating bracket",
      inviteOrSchedule: "Invite teams or create match schedule",
    },
    roster: {
      title: "Team roster",
      countLabel: "{count}/{max} players",
      inviteCode: "Invite code",
      addMember: "Add member",
      addFromPrevious: "Select from previous tournament",
      memberName: "Member name",
      gender: "Gender",
      male: "Male",
      female: "Female",
      skillLevel: "Skill level",
      optional: "Optional",
      removeMember: "Remove member?",
      removeMemberDesc: "Are you sure you want to remove {name} from the team?",
      rosterFull: "Team roster is full",
      captain: "Captain",
      noMembers: "No members yet",
      addedSuccess: "Added {count} members",
    },
    standings: {
      rank: "#",
      team: "Team",
      played: "P",
      won: "W",
      lost: "L",
      points: "Pts",
      games: "Games",
      diff: "+/-",
      legend: {
        played: "Played",
        won: "Won",
        lost: "Lost",
        points: "Points (Win = 1pt)",
        diff: "Difference",
      },
    },
    dialog: {
      generateMatches: "Generate match schedule?",
      generateMatchesDesc: "The system will automatically create round robin matches for approved teams.",
      teamsCount: "{count} teams",
      matchesCount: "{count} matches",
      roundsCount: "{count} rounds",
      gamesPerMatch: "{count} games/match",
      minTeamsRequired: "Need at least 2 approved teams to generate schedule",
      incompleteRosters: "Cannot generate - Some teams have incomplete rosters",
      confirm: "Confirm",
    },
  },
  // Doubles Elimination
  doublesElimination: {
    title: "Doubles Elimination",
    description: "Double elimination format with loser's bracket for 32+ teams",
    createNew: "Create new",
    loginRequired: "Login to create tournament",
    loginRequiredDesc: "You need to login to create and manage Doubles Elimination tournaments",
    noTournaments: "No tournaments yet",
    noTournamentsDesc: "Create your first Doubles Elimination tournament",
    viewBtn: "View",
    teams: "teams",
    earlyRounds: "Early rounds",
    semifinalPlus: "Semis+",
    status: {
      setup: "Setting up",
      ongoing: "In progress",
      completed: "Completed",
    },
    format: {
      bo1: "BO1",
      bo3: "BO3",
      bo5: "BO5",
    },
    about: {
      title: "About Doubles Elimination",
      description: "Doubles Elimination is an improved elimination format that gives teams a second chance after losing in the first round.",
      round1: "All teams compete. Lose → drop to loser's bracket",
      round2: "R1 losers compete. Lose again → eliminated",
      round3: "Winner + loser bracket merge, normalize to 2^n teams",
      round4Plus: "Standard Single Elimination to finals",
      minTeams: "Minimum teams: 32.",
      suggestion: "Suggested: 32, 40, 48, 64, 80, 96, 128 teams.",
    },
    setup: {
      title: "Create Doubles Elimination Tournament",
      tournamentName: "Tournament name",
      tournamentNamePlaceholder: "E.g.: Summer Pickleball 2024",
      teamCount: "Number of teams",
      minTeamsError: "Minimum 32 teams required",
      courtCount: "Number of courts",
      startTime: "Start time",
      earlyRoundsFormat: "Early rounds format (R1-R2)",
      semifinalsFormat: "Semifinals format",
      finalsFormat: "Finals format",
      thirdPlaceMatch: "Third place match",
      thirdPlaceMatchDesc: "Semifinal losers will play for 3rd place",
      creating: "Creating...",
      createBtn: "Create tournament",
      shuffled: "Shuffled team order",
      nameRequired: "Please enter tournament name",
      need32Teams: "Need at least 32 teams",
      createError: "Error creating tournament",
      addTeamsError: "Error adding teams",
      bracketError: "Error generating bracket",
      createSuccess: "Tournament created!",
      hints: {
        byeCount: "{count} teams get a BYE to R2",
        r1Matches: "{count} matches in R1",
        r2Matches: "{count} matches in R2",
      },
    },
    scoring: {
      match: "Match",
      court: "Court",
      time: "Time",
      vs: "vs",
      tbd: "TBD",
      edit: "Edit",
      score: "Score",
      waiting: "Waiting for 2 teams",
      winner: "Winner",
      saveSuccess: "Score saved",
      saveError: "Error saving score",
    },
    view: {
      notFound: "Tournament not found",
      backToList: "Back to list",
      preliminary: "Preliminary",
      playoff: "Playoff",
      teams: "Teams",
      settings: "Settings",
      teamList: "Team list",
      tournamentSettings: "Tournament settings",
      thirdPlaceMatch: "Third place match",
      courts: "Courts",
      yes: "Yes",
      no: "No",
      share: "Share",
      copied: "Copied",
      copyError: "Could not copy",
      deleteConfirm: "Delete tournament?",
      deleteConfirmDesc: "This action cannot be undone. All data will be permanently deleted.",
      deleteSuccess: "Tournament deleted",
      deleteError: "Error deleting tournament",
      eliminatedRound: "Eliminated R",
      r3AssignedTitle: "R3 assigned",
      r3TiedDesc: "{count} players tied on point diff ({names}). System randomly paired 2 players for preliminary match.",
      r3NormalDesc: "Players have been assigned to next rounds based on point differential.",
    },
    bracket: {
      round: "Round",
      match: "Match",
      winnerBracket: "Winner Bracket",
      loserBracket: "Loser Bracket",
      finalElimination: "Final Elimination",
      champion: "Champion",
      finals: "Final",
      thirdPlace: "3rd Place",
      quarterFinal: "Quarter-final",
      semiFinal: "Semi-final",
      round16: "Round of 16",
      done: "Done",
      openScoring: "Score",
      editScore: "Edit",
      assignR3: "Assign R3",
      assigning: "Assigning...",
      waitingAssignment: "Waiting for assignment",
      waitingR1R2: "Waiting for R1 & R2",
      noMatches: "No matches",
      noBracket: "No bracket yet. Complete setup to generate bracket.",
      playoffNotReady: "Playoff will start after preliminary rounds are completed.",
      tieNotAllowed: "Scores cannot be tied",
      loserOf: "Loser of Match",
      clickGameToEdit: "Click on a game slot to edit score",
      waitingTeams: "Waiting for 2 teams to score",
      finalBadge: "F",
      scoreSaved: "Score saved",
      matchSaved: "Match result saved",
      gameSaved: "Game saved",
      scoreSaveError: "Error saving score",
      playoffCreated: "Playoff created",
      playoffCreatedDesc: "Playoff bracket has been generated with correct seeding.",
      r3Assigned: "R3 assigned",
      r3AssignedDesc: "Players assigned to next round.",
      cancel: "Cancel",
      save: "Save",
      saveGameN: "Save G",
    },
  },
  news: {
    title: "Pickleball News",
    subtitle: "Curated pickleball updates from trusted sources.",
    noNews: "No updates yet. News will appear here when available.",
    readMore: "Read more",
  },
  seo: {
    tools: {
      title: "Pickleball Tournament Tools",
      description: "This page provides tools to organize pickleball tournaments easily. Supports multiple tournament formats. Designed for speed, simplicity, and accuracy. Suitable for casual, club, and competitive tournaments.",
      formatsTitle: "Supported Tournament Formats",
      quickBracket: "Quick Bracket – Round-robin scoring + Playoff for casual tournaments",
      teamMatch: "Team Match (MLP) – Team-based competition like Major League Pickleball",
      singleElimination: "Single Elimination – Direct knockout, one loss and you're out",
      doublesElimination: "Doubles Elimination – 2 losses to be eliminated, fairer for competitors",
      flexTournament: "Flex Tournament – Create your own tournament format with no restrictions",
    },
    doublesElimination: {
      title: "Doubles Elimination – 2 Losses to Be Eliminated",
      description: "Double elimination format increases fairness for tournaments. First loss → loser's bracket. Second loss → eliminated. Winner and loser brackets merge before finals.",
      whenToUseTitle: "When should you use Doubles Elimination?",
      whenToUseDesc: "Suitable for medium to large tournaments, competitive or semi-pro events, when teams deserve a second chance to prove themselves.",
      sizeTitle: "Recommended Tournament Size",
      sizeDesc: "Minimum teams: 32. Suggested sizes: 32, 40, 48, 64, 80, 96, 128 teams for balanced brackets.",
    },
    flexTournament: {
      title: "Flex Tournament – Create Your Own Tournament",
      description: "User-defined tournament format. No enforced rules or structure. Full control over players, teams, groups, matches. Ideal for custom or experimental tournaments.",
      differenceTitle: "What makes Flex Tournament different?",
      differenceList: [
        "No predefined bracket logic",
        "Maximum flexibility in structure",
        "Organizer has full control",
        "Supports custom tracking",
      ],
      whoTitle: "Who should use Flex Tournament?",
      whoDesc: "Club organizers, training tournaments, special formats, and custom rules events.",
    },
  },
  referee: {
    title: "Referees",
    emailPlaceholder: "Enter referee email",
    addBtn: "Add",
    emptyState: "No referees yet. Add referees so they can enter scores.",
    noName: "No name",
    removeBtn: "Remove",
    description: "Referees can enter and edit scores for all matches in the tournament.",
    loading: "Loading...",
  },
  teamMatchComponents: {
    statusPending: "Pending",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    yourTeam: "Your team",
    youAreCaptain: "You are the captain of this team",
    members: "Members",
    rosterFull: "Full roster",
    rosterIncomplete: "Incomplete",
    memberList: "Member list:",
    male: "Male",
    female: "Female",
    manageTeam: "Manage team",
    manageAsOrganizer: "Manage team as organizer",
    viewTeamInfo: "View team info",
    noTeams: "No teams yet",
    deleteTeamTitle: "Delete team?",
    deleteTeamDesc: "Are you sure you want to delete \"{name}\"? This action cannot be undone.",
    cancelBtn: "Cancel",
    deleteBtn: "Delete",
    teamListTitle: "Team list",
    registeredTeams: "Tournament has {count} registered teams",
    rosterStatus: "Status",
    lineupDone: "Lineup done",
    noMatchesInGroup: "No matches in",
    noMatchesCreateSchedule: "Create groups to generate match schedule",
    noGroupsYet: "No groups yet",
    roundLabel: "Round",
    startRound: "Start",
    waitingLineup: "Waiting lineup",
    missingLineup: "Missing lineup:",
    scoreBtn: "Score",
    notStarted: "Not started",
    liningUp: "Lining up",
    live: "LIVE",
    ended: "Ended",
    createTeamTitle: "Create new team",
    createTeamDesc: "Register your team to join the tournament. You will be the captain.",
    teamNameLabel: "Team name",
    teamNameError: "Team name must be at least 2 characters",
    captainNameLabel: "Captain name (you)",
    captainNameError: "Captain name must be at least 2 characters",
    inviteCaptainEmail: "Enter captain email to invite a team. Team will be auto-approved.",
    inviteCaptainEmailLabel: "Captain email",
    inviteDesc: "Invite team",
    inviteEmailError: "Please enter captain email",
    groupSetupTitle: "Create groups",
    groupSetupDesc: "Choose number of groups and preview team distribution",
  },
  dashboard: {
    title: "Live Dashboard",
    description: "Track court status and scores in realtime",
    selectTournament: "Select a tournament",
    court: "Court",
    nowPlaying: "Now Playing",
    nextMatch: "Next",
    available: "Available",
    tvMode: "TV Mode",
    exitTvMode: "Exit TV Mode",
    soundOn: "Sound On",
    soundOff: "Sound Off",
    noActiveTournaments: "No active tournaments",
    vs: "vs",
    match: "Match",
    score: "Score",
    quickTable: "Quick Table",
    teamMatch: "Team Match",
    doublesElimination: "Doubles Elimination",
    autoRotate: "Auto rotate",
    liveMatches: "Live Matches",
    upNext: "Up Next",
    completed: "Completed",
    backToList: "Back",
  },
};
